import { getConfiguration } from '../shared/utils/configuration';
import Axios from 'axios';
import { getRequestHeaders } from './utils';
import { normalizeCampaignCategory } from '../shared/utils/campaignTypes';
import {
  FileResponse,
  OrdersApiClient,
  OrderV2,
  PreOrderCountDetailsDto,
  QuickSearchOrdersRequest,
  UserRolesResponse,
} from '@kotipizzagroup/kotipizza-orders-api-client';
import { Dayjs } from 'dayjs';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

export const orderApiQueryKeys = {
  singleOrder: 'single-order',
  orderSearch: 'order-search',
};

let client: OrdersApiClient | null = null;

const getApiClient = (): OrdersApiClient => {
  if (client) return client;

  const baseUrl = getConfiguration<string>('ordersApi.root', '');

  // orders-api-client 2.7.0 stopped JSON.parse-ing response bodies in its generated
  // processXxx methods and now expects axios to have parsed the JSON already, so this
  // instance must keep axios's default parsing (no `transformResponse: []`). The receipt
  // PDF endpoint is unaffected: it asks for `responseType: 'blob'`, which axios never
  // runs its JSON transform over.
  const instance = Axios.create({
    timeout: 10000,
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  client = new OrdersApiClient(baseUrl, instance);
  return client;
};

export const getOrderById = async (orderId: number): Promise<OrderV2> => {
  const client = getApiClient();
  const order = await client.getOrderById(orderId);
  const discountDetails = order.shoppingCartPrice?.discountDetails?.map((discount) =>
    discount.campaign ? { ...discount, campaign: normalizeCampaignCategory(discount.campaign) } : discount
  );
  return discountDetails ? { ...order, shoppingCartPrice: { ...order.shoppingCartPrice, discountDetails } } : order;
};

export const useSingleOrder = (
  orderId: number,
  options = getDefaultOptions<OrderV2>()
): UseQueryResult<OrderV2, unknown> =>
  useQuery<OrderV2>([orderApiQueryKeys.singleOrder, orderId], () => getOrderById(orderId), options);

export const downloadOrderReceiptPdf = async (orderId: string): Promise<FileResponse> => {
  const client = getApiClient();
  return await client.generateOrderReceiptPdfById(parseInt(orderId));
};

export const quickSearchOrders = async (search: QuickSearchOrdersRequest): Promise<OrderV2[]> => {
  const client = getApiClient();
  return await client.quickSearchOrders(search);
};

const getOrdersForShop = async (shopExternalId: string, date: Dayjs): Promise<OrderV2[]> => {
  const client = getApiClient();
  const data = await client.getAllShopOrdersForDay(shopExternalId, date.startOf('day').format());

  return data;
};

export const useRestaurantPreorders = (shopExternalId: string): UseQueryResult<PreOrderCountDetailsDto[]> => {
  return useQuery(['restaurant-preorders', shopExternalId], () => getPreordersForShop(shopExternalId));
};

export const useRestaurantOrders = (
  shopExternalId: string,
  date: Dayjs,
  options: Options<OrderV2[]> = {}
): UseQueryResult<OrderV2[]> => {
  // The day is the cache identity, not the `Dayjs` instance: the fetcher normalises to
  // `startOf('day')`, so keying on the formatted day keeps a caller that passes a fresh `dayjs()`
  // from churning the key on every render.
  return useQuery(
    ['restaurant-orders', shopExternalId, date.format('YYYY-MM-DD')],
    () => getOrdersForShop(shopExternalId, date),
    options
  );
};

export const getPreordersForShop = async (shopExternalId: string): Promise<PreOrderCountDetailsDto[]> => {
  const client = getApiClient();
  const data = await client.getShopPreOrderCounts(shopExternalId);

  return data.preOrderCounts;
};

export const getOrdersRoles = async (): Promise<UserRolesResponse> => {
  const client = getApiClient();
  return await client.getUserRoles();
};

type Options<T> = Parameters<typeof useQuery<T>>['2'];

const getDefaultOptions = <T>(): Options<T> => ({
  enabled: true,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
});
